// import * as ga from '@/lib/ga'
// import * as fpixel from '@/lib/fpixel'
import * as gtm from '@/lib/gtm'
import { ArticleModel, CartModel, OrderConfirmationInfoModel, OrderCreatedModel, OrderInfoModel, SveaCheckoutResponseModel } from '@/types/api'
import { useLazyGetOrderQuery } from '@/api/order'
import { useAppSelector } from './useAppSelector'
import { selectCurrency } from '@/features/app/appSlice'
import { useRef } from 'react'

const useAnalyticsManager = () => {
  const [getOrder, orderResult] = useLazyGetOrderQuery()
  const selectedCurrency = useAppSelector(selectCurrency)
  const loadingOrder = useRef<boolean>(false)

  const pageView = (url: string) => {
    gtm.pageView(url)
  }

  const addToCart = (articleId: number, articleText: string, quantity: number, price: number) => {
    gtm.addToCart(articleId, articleText, quantity, price, selectedCurrency)
  }

  const search = (searchTerm: string) => {
    gtm.search(searchTerm)
  }

  const viewProduct = (article: ArticleModel) => {
    gtm.viewItem(article.id, article.articleText, article.price, selectedCurrency)
  }

  const viewProductList = (articles: ArticleModel[], listId: string, listName: string) => {
    gtm.viewItemList(listId, listName, articles.map(article => {
      return {
        item_id: article.id,
        item_name: article.articleText,
        price: article.price
      }
    }))
  }

  const clickProduct = (article: ArticleModel, listId: string, listName: string) => {
    gtm.selectItem(listId, listName, {
      item_id: article.id,
      item_name: article.articleText,
      price: article.price
    })
  }

  const viewCart = (cart: CartModel) => {
    gtm.viewCart(getTotalAmount(cart), selectedCurrency, cart.rows.map(row => {
      return {
        item_id: row.articleId,
        item_name: row.articleText,
        quantity: row.quantity,
        price: row.price
      }
    }))
  }

  const initiateCheckout = (cart: CartModel) => {
    const items = cart.rows.map(row => {
      return {
        item_id: row.articleId,
        item_name: row.articleText,
        quantity: row.quantity,
        price: row.price
      }
    })

    gtm.beginCheckout(getTotalAmount(cart), selectedCurrency, items)
  }

  const initiateSveaCheckout = (checkout: SveaCheckoutResponseModel) => {
    const items = checkout.rows.map(row => {
      return {
        item_id: row.articleId,
        item_name: row.description,
        quantity: row.quantity,
        price: row.price
      }
    })

    gtm.beginCheckout(getTotalCheckoutAmount(checkout), checkout.currencyCode, items)
  }

  const addPaymentInfo = (paymentMethod: string, cart: CartModel) => {
    gtm.addPaymentInfo(paymentMethod, cart.totalAmount, selectedCurrency, cart.rows.map(row => {
      return {
        item_id: row.articleId,
        item_name: row.articleText,
        quantity: row.quantity,
        price: row.price
      }
    }))
  }

  const changeDeliveryAddress = () => {

  }

  const purchase = async (order: OrderConfirmationInfoModel) => {
    gtm.purchase(getTotalOrderAmount(order), order.currencyCode, order.rows.map(row => {
      return {
        item_id: row.articleId,
        item_name: row.description,
        quantity: row.quantity,
        price: row.price
      }
    }))
  }

  const login = (userId: string) => {
    gtm.login(userId)
  }

  const register = (userId: string) => {
    gtm.signUp(userId)
  }

  const getNetAmount = (totalAmount: number, shippingPrice: number) => {
    return totalAmount - shippingPrice
  }

  const getTotalAmount = (cart: CartModel) => {
    return getNetAmount(cart.totalAmount, cart.shippingPrice)
  }

  const getTotalOrderAmount = (order: OrderConfirmationInfoModel) => {
    return getNetAmount(order.totalAmount, order.shippingPrice)
  }

  const getTotalCheckoutAmount = (checkout: SveaCheckoutResponseModel) => {
    return getNetAmount(checkout.totalAmount, checkout.shippingPrice)
  }

  return {
    pageView,
    addPaymentInfo,
    addToCart,
    search,
    viewProduct,
    viewProductList,
    clickProduct,
    viewCart,
    initiateCheckout,
    initiateSveaCheckout,
    purchase,
    changeDeliveryAddress,
    login,
    register
  }
}

export default useAnalyticsManager